'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { isOnCanvas, type WorkspaceItemDTO } from '@/lib/workspaces/types';
import {
  entryOfItem,
  orderScreens,
} from '@/lib/workspaces/screens';
import { displayScreenEntries } from '@/lib/workspaces/stage-rules';
import { detectPlatform, matchShortcut } from '@/lib/workspace/commands';
import { cn } from '@/lib/utils';
import {
  useCurrentScreenId,
  useDesktopFullscreen,
  useFullscreenItemId,
  useWorkspaceActions,
  useWorkspaceItems,
} from './store';
import {
  resolveItemLabel,
  resolveScreenEntryLabel,
  useLiveContentMeta,
} from './item-label';
import { isEditableFocus } from './use-workspace-shortcuts';

/** How long the HUD strip stays up after the last switch keypress. */
const HUD_LINGER_MS = 1400;

/**
 * Windows the FULLSCREEN switcher swaps between: on-canvas app windows
 * in the spatial order. Blank screens can't hold the fullscreen
 * takeover, so they are excluded here — on the canvas the switcher
 * walks SCREEN ENTRIES instead (see `step`).
 */
function fullscreenCandidates(items: WorkspaceItemDTO[]): WorkspaceItemDTO[] {
  return orderScreens(items).filter(
    (it) => it.type !== 'board' && isOnCanvas(it.state),
  );
}

/**
 * ⌘⇧←/→ app switcher (Ctrl+Shift on non-mac).
 *
 * Instant-switch model: each press glides to the neighbouring screen in
 * the shared screen order (via the `ws:screen` chokepoint, which frames
 * the window and parks the camera on it), and flashes a small HUD strip
 * naming every app with the active one highlighted. While a widget
 * holds the fullscreen takeover the same chord swaps WHICH app is
 * fullscreen, in place — no exit / re-enter dance.
 *
 * Two event sources feed the same handler:
 *   - a document-level `keydown` (canvas / host chrome owns focus);
 *   - the `workspace:host-key` CustomEvent relayed by `WidgetBridge`
 *     when the chord was pressed INSIDE a widget iframe (the SDK
 *     forwards a small allowlist of host-reserved chords, because key
 *     events never cross the iframe boundary on their own).
 *
 * Deliberately NOT plain ⌘←/→: that's browser Back/Forward on macOS
 * whenever focus isn't in a text field, and we can't intercept it once
 * an iframe owns focus — one miss would navigate away from the
 * workspace. There is no hold-⌘-to-preview mechanic either: the Meta
 * keyup is unobservable while an iframe has focus, so a commit-on-
 * release switcher would strand its overlay open.
 *
 * Mounted once inside `WorkspaceCanvas`.
 */
export function AppSwitcher() {
  const items = useWorkspaceItems();
  const fullscreenItemId = useFullscreenItemId();
  const currentScreenId = useCurrentScreenId();
  const { bringToFront, setFullscreenItem, setCurrentScreen } =
    useWorkspaceActions();

  const [hud, setHud] = useState<{
    apps: { id: string; label: string }[];
    activeId: string;
  } | null>(null);
  const hudTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // HUD names resolve live-first through item-label.ts, the same rule as
  // the rail and the dock. The local copy of the lookup this replaced read
  // only the stored item snapshot, so a renamed app flashed its OLD name
  // here (the 2026-09-13 browser pass). Read-only: nothing is written back.
  const live = useLiveContentMeta();

  // Refs so the (stable) event handlers always see the live snapshot
  // without re-binding listeners on every items/camera change.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const liveRef = useRef(live);
  liveRef.current = live;
  const fullscreenRef = useRef(fullscreenItemId);
  fullscreenRef.current = fullscreenItemId;
  const currentScreenRef = useRef(currentScreenId);
  currentScreenRef.current = currentScreenId;
  // The windows this device shows full screen, each on a screen of its own
  // (lane D; the Stage algorithm, rule 17: "the ⌘⇧ arrows, the digits and
  // Stage move between them, as full-screen Spaces do on a Mac").
  const desktopFullscreen = useDesktopFullscreen();
  const desktopFullscreenRef = useRef(desktopFullscreen);
  desktopFullscreenRef.current = desktopFullscreen;

  const step = useCallback(
    (dir: 1 | -1) => {
      const showHud = (
        apps: { id: string; label: string }[],
        activeId: string,
      ) => {
        setHud({ apps, activeId });
        if (hudTimer.current) clearTimeout(hudTimer.current);
        hudTimer.current = setTimeout(() => {
          hudTimer.current = null;
          setHud(null);
        }, HUD_LINGER_MS);
      };

      const fsId = fullscreenRef.current;
      if (fsId) {
        // Fullscreen: swap the takeover in place between app windows.
        // Also raise the new app so exiting fullscreen leaves it on top
        // of the stack, and park the camera position on it so exit +
        // arrows anchor right.
        const apps = fullscreenCandidates(itemsRef.current);
        if (apps.length === 0) return;
        const currentIdx = apps.findIndex((it) => it.id === fsId);
        const nextIdx =
          currentIdx < 0
            ? dir === 1
              ? 0
              : apps.length - 1
            : (currentIdx + dir + apps.length) % apps.length;
        const next = apps[nextIdx];
        setFullscreenItem(next.id);
        bringToFront(next.id);
        setCurrentScreen(next.id);
        showHud(
          apps.map((it) => ({
            id: it.id,
            label: resolveItemLabel(it, liveRef.current, it.type),
          })),
          next.id,
        );
        return;
      }

      // Canvas (Part IV): the switcher walks SCREEN ENTRIES — the same
      // left-to-right list the ⌘⇧digit chords, dots and overview use.
      // A split is one stop, a blank screen is one stop, and so is each
      // window this device shows full screen (its temporary screen).
      const entries = displayScreenEntries(
        itemsRef.current,
        desktopFullscreenRef.current.map((r) => r.id),
      );
      if (entries.length === 0) return;
      const current = entryOfItem(entries, currentScreenRef.current);
      let currentIdx = current ? entries.indexOf(current) : -1;
      if (currentIdx < 0) {
        // Fallback anchor: the entry holding the top-of-stack window
        // (click-to-front keeps top-z ≈ "the screen the user is in").
        let topZ = -Infinity;
        for (const it of itemsRef.current) {
          if (!isOnCanvas(it.state) || it.type === 'board') continue;
          const z = it.state.zIndex ?? 0;
          const e = entryOfItem(entries, it.id);
          if (e && z >= topZ) {
            topZ = z;
            currentIdx = entries.indexOf(e);
          }
        }
      }
      const nextIdx =
        currentIdx < 0
          ? dir === 1
            ? 0
            : entries.length - 1
          : (currentIdx + dir + entries.length) % entries.length;
      const next = entries[nextIdx];

      // The ws:screen chokepoint frames the neighbor (fit + margin,
      // camera parked) and raises app windows (blank screens stay at
      // the back of the stack).
      window.dispatchEvent(
        new CustomEvent('ws:screen', {
          detail: { kind: 'item', itemId: next.id },
        }),
      );
      showHud(
        entries.map((e) => ({
          id: e.id,
          label: resolveScreenEntryLabel(e, itemsRef.current, liveRef.current),
        })),
        next.id,
      );
    },
    [bringToFront, setFullscreenItem, setCurrentScreen],
  );

  useEffect(() => {
    const platform = detectPlatform();
    const onKey = (e: KeyboardEvent) => {
      // ⌘⇧←/→ selects to line start/end inside text fields — never
      // hijack it while the user is typing in host chrome (iframes
      // never surface here; their keys arrive via the host-key relay
      // below, which the SDK only sends when the widget didn't want
      // the chord).
      if (e.isComposing || isEditableFocus()) return;
      const dir = matchShortcut(
        { key: 'ArrowRight', mod: true, shift: true },
        e,
        platform,
      )
        ? 1
        : matchShortcut({ key: 'ArrowLeft', mod: true, shift: true }, e, platform)
          ? -1
          : 0;
      if (!dir) return;
      e.preventDefault();
      e.stopPropagation();
      step(dir);
    };
    const onHostKey = (e: Event) => {
      const key = (e as CustomEvent<{ key?: string }>).detail?.key;
      if (key === 'ArrowRight') step(1);
      else if (key === 'ArrowLeft') step(-1);
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('workspace:host-key', onHostKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('workspace:host-key', onHostKey);
      if (hudTimer.current) clearTimeout(hudTimer.current);
    };
  }, [step]);

  if (!hud) return null;

  return (
    // z-[230]: above the fullscreen takeover portal (z-200) and the
    // rail's fullscreen lift (z-210) so the strip reads in every mode.
    <div
      aria-hidden
      className="pointer-events-none fixed left-1/2 top-6 z-[230] -translate-x-1/2"
    >
      <div className="liquid-glass flex max-w-[min(80vw,720px)] items-center gap-1 overflow-hidden rounded-2xl border border-border/40 px-2 py-1.5 shadow-xl">
        {hud.apps.map((app) => (
          <span
            key={app.id}
            className={cn(
              'max-w-[16ch] truncate rounded-lg px-2.5 py-1 text-[12px] leading-none whitespace-nowrap',
              app.id === hud.activeId
                ? 'bg-primary/85 font-medium text-primary-foreground'
                : 'text-foreground/60',
            )}
          >
            {app.label}
          </span>
        ))}
      </div>
    </div>
  );
}
